export type UserRole = "client" | "admin" | "reviewer";

export type OrderStatus = "pending" | "paid" | "failed" | "refunded" | "cancelled";
export type SubmissionStatus = "draft" | "submitted" | "in_review" | "ready" | "cancelled";

export interface PublicUser {
  id: string;
  email: string;
  fullName: string;
  companyName: string | null;
  phone: string | null;
  abn: string | null;
  role: UserRole;
  logoR2Key: string | null;
  emailVerified: boolean;
}

export interface ResourceCategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  sortOrder: number;
}

export interface Resource {
  id: string;
  slug: string;
  title: string;
  summary: string;
  description: string | null;
  categoryId: string | null;
  categoryName?: string | null;
  priceCents: number;
  isFree: boolean;
  isPublished: boolean;
  fileName: string | null;
  coverImageUrl: string | null;
  tags: string | null;
  downloadCount: number;
  /** Site default is aud. The five Singapore paid DIY SKUs are sgd. */
  currency?: "aud" | "sgd";
}

export interface ReportType {
  id: string;
  slug: string;
  name: string;
  summary: string;
  description: string | null;
  priceCents: number;
  turnaroundHours: number;
  questionnaireSchema: QuestionnaireSchema;
  templateKey?: string | null;
  brand: "ecological_engineering";
  isPublished: boolean;
}

export interface QuestionnaireField {
  id: string;
  label: string;
  type: "text" | "textarea" | "number" | "select" | "checkbox";
  required?: boolean;
  options?: string[];
}

export interface QuestionnaireStep {
  id: string;
  title: string;
  fields: QuestionnaireField[];
}

export interface QuestionnaireSchema {
  steps: QuestionnaireStep[];
}

export interface SubmissionSummary {
  id: string;
  reportTypeId: string;
  reportTypeName?: string;
  status: SubmissionStatus;
  projectName: string | null;
  submittedAt: string | null;
  readyAt: string | null;
  createdAt: string;
  updatedAt: string;
  totalCents?: number;
}

export interface OrderSummary {
  id: string;
  orderType: string;
  status: OrderStatus;
  currency: string;
  subtotalCents: number;
  gstCents: number;
  totalCents: number;
  invoiceNumber: string | null;
  createdAt: string;
  paidAt: string | null;
  items?: Array<{ title: string; unitPriceCents: number; quantity: number }>;
}

export interface PricingTier {
  diy: { label: string; blurb: string };
  diwy: { label: string; blurb: string; turnaround: string };
  dify: { label: string; blurb: string };
  urbanDevelopmentPerLotCents: number;
  consultancyFromCents: number;
}

/** Controlled document kinds for the internal IMS register. */
export type DocumentKind =
  | "diy_template"
  | "report_template"
  | "checklist"
  | "canvas"
  | "procedure"
  | "policy"
  | "form"
  | "capability"
  | "profile";

export type DocumentRegisterStatus = "draft" | "active" | "withdrawn" | "archived";

export type DocumentRevisionStatus =
  | "draft"
  | "in_review"
  | "approved"
  | "issued"
  | "superseded"
  | "obsolete";

/** Master row in the internal template / version control register. */
export interface DocumentRegisterEntry {
  id: string;
  docId: string;
  title: string;
  kind: DocumentKind | string;
  domainCode: string | null;
  classification: string;
  resourceId: string | null;
  resourceTitle?: string | null;
  resourceSlug?: string | null;
  reportTypeId: string | null;
  status: DocumentRegisterStatus | string;
  currentRevisionId: string | null;
  currentRevision?: string | null;
  currentIssueDate?: string | null;
  currentRevStatus?: string | null;
  currentFileName?: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DocumentRevision {
  id: string;
  documentId: string;
  revision: string;
  issueDate: string;
  changeSummary: string;
  status: DocumentRevisionStatus | string;
  supersedesRevisionId: string | null;
  preparedBy: string | null;
  reviewedBy: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  fileName: string | null;
  fileR2Key: string | null;
  fileSizeBytes: number | null;
  pageCount: number | null;
  generatorScript: string | null;
  imsAlignmentNote: string | null;
  createdAt: string;
}

export const GST_RATE = 0.1;

/** Tier 3 consultancy engagements start from this amount (ex GST). */
export const CONSULTANCY_FROM_CENTS = 750_000; // $7,500 ex GST

/** Optional lot-scale package line (subdivision / urban) — still Tier 3 scoped work. */
export const URBAN_DEVELOPMENT_PER_LOT_CENTS = 300_000; // $3,000 ex GST per lot

/** Lowest Tier 2 guided report (ex GST) — for “start here” messaging. */
export const GUIDED_REPORT_FROM_CENTS = 125_000;

/** Highest listed DIY tool (incl. GST). Paid PDFs sit below this; Plant species ID is A$149.00. */
export { DIY_TOOLS_MAX_CENTS, PLANT_SPECIES_ID_PRICE_CENTS, PLANT_SPECIES_ID_SLUG } from "./plantSpeciesId";

/** Market region for service catalogues. */
export type ServiceRegion = "au" | "sg";

/** How the client can buy / engage for each offering. */
export type DeliveryMode = "diy" | "diwy" | "dify";

export interface ServiceTierCopy {
  /** DIY — download tools / worksheets */
  diy: string;
  /** DIWY — guided report / co-delivery (~48h where listed) */
  diwy: string;
  /** DIFY — full consultancy / we deliver */
  dify: string;
}

/** Platform-wide delivery model labels (marketing + UX). */
export const DELIVERY_MODEL = {
  diy: {
    code: "DIY",
    name: "Do it yourself",
    path: "/resources",
    blurb: "Templates, checklists and worksheets — download and go.",
  },
  diwy: {
    code: "DIWY",
    name: "Do it with you",
    path: "/reports",
    blurb: "Guided packs with specialist review — typically ~48 hours.",
  },
  dify: {
    code: "DIFY",
    name: "Do it for you",
    path: "/contact",
    blurb: "Full scoped consultancy when risk or complexity demands it.",
  },
} as const;

export const REGIONS: Record<
  ServiceRegion,
  { code: ServiceRegion; name: string; path: string; tagline: string; focus: string[] }
> = {
  au: {
    code: "au",
    name: "Australia",
    path: "/au/services",
    tagline: "Environmental engineering that unblocks sites, approvals and construction.",
    focus: [
      "Soils, ASS & ESC",
      "Ecology, offsets & PMAV",
      "Bushfire risk & BHMP",
      "CEMP & compliance",
      "Approvals, urban packages and mining Standard EA",
    ],
  },
  sg: {
    code: "sg",
    name: "Singapore",
    path: "/sg/services",
    tagline: "MEP and building performance for tropical commercial assets.",
    focus: [
      "ASHRAE Level I–III energy audits",
      "Performance metrics & Green Mark",
      "Energy monitoring & optimisation",
      "ACMV / chiller plant",
      "BMS, metering, M&V & RCx",
    ],
  },
};

/** Service group for Services page sections (internal taxonomy). */
export type ServiceGroupId =
  | "soils-water"
  | "ecology-biodiversity"
  | "vegetation-land"
  | "bushfire"
  | "construction-compliance"
  | "approvals-development"
  | "mining"
  | "mep-singapore";

export const SERVICE_GROUPS: {
  id: ServiceGroupId;
  region: ServiceRegion;
  title: string;
  blurb: string;
}[] = [
  {
    id: "soils-water",
    region: "au",
    title: "Soils, water & coastal",
    blurb: "ASS, soils, ESC, WSUD, marine plants and blue carbon framing.",
  },
  {
    id: "ecology-biodiversity",
    region: "au",
    title: "Ecology & biodiversity",
    blurb: "Species ID, surveys, offsets and habitat evidence for approvals.",
  },
  {
    id: "vegetation-land",
    region: "au",
    title: "Vegetation & land management",
    blurb: "PMAV, vegetation management and related land-clearing pathways.",
  },
  {
    id: "bushfire",
    region: "au",
    title: "Bushfire",
    blurb: "Risk assessment and hazard management plans that feed design and DA.",
  },
  {
    id: "construction-compliance",
    region: "au",
    title: "Construction & compliance",
    blurb: "CEMPs, site plans and monitoring that hold up during delivery.",
  },
  {
    id: "approvals-development",
    region: "au",
    title: "Approvals & development",
    blurb: "Pathway strategy and urban packages that keep programmes moving.",
  },
  {
    id: "mining",
    region: "au",
    title: "Mining",
    blurb: "Standard Environmental Authority applications and related mining approvals work.",
  },
  {
    id: "mep-singapore",
    region: "sg",
    title: "MEP & building performance",
    blurb:
      "Mechanical, electrical and plumbing for Singapore buildings — metrics, energy, Green Mark and FM-ready packs.",
  },
];

function envTiers(topic: string): ServiceTierCopy {
  return {
    diy: `${topic} templates, checklists and field sheets you can use today.`,
    diwy: `Guided ${topic.toLowerCase()} pack with specialist review (typically ~48h).`,
    dify: `Full ${topic.toLowerCase()} consultancy — investigation, design support and delivery.`,
  };
}

function mepTiers(topic: string): ServiceTierCopy {
  return {
    diy: `${topic} worksheets, KPI sheets and self-assessment checklists.`,
    diwy: `Guided ${topic.toLowerCase()} review with specialist interpretation of your data.`,
    dify: `Full ${topic.toLowerCase()} programme — analysis, optimisation and documented outcomes.`,
  };
}

export interface ServiceOffering {
  id: string;
  region: ServiceRegion;
  group: ServiceGroupId;
  name: string;
  short: string;
  blurb: string;
  tiers: ServiceTierCopy;
  /** Named public fee in cents (fee portion). */
  listedPriceCents?: number;
  /** When true, show the fee as + GST, not ex GST. */
  listedPricePlusGst?: boolean;
}

export const SERVICES: ServiceOffering[] = [
  // —— Australia ——
  {
    id: "ass",
    region: "au",
    group: "soils-water",
    name: "Acid Sulfate Soils",
    short: "ASS",
    blurb:
      "When excavation risk is unclear, we help you investigate, rate risk and lock a defensible management plan before costs explode.",
    tiers: envTiers("ASS"),
  },
  {
    id: "esc",
    region: "au",
    group: "soils-water",
    name: "Erosion & Sediment Control",
    short: "ESC / CPESC",
    blurb:
      "When controls fail on the first wet week, we fix design and field practice so plans survive construction — not just the drawing set.",
    tiers: envTiers("ESC"),
  },
  {
    id: "soils",
    region: "au",
    group: "soils-water",
    name: "Soils",
    short: "Soils",
    blurb:
      "When cut/fill and reuse decisions are guesswork, we bring classification, logging and management logic that stands up on site and in review.",
    tiers: envTiers("Soils"),
  },
  {
    id: "wsud",
    region: "au",
    group: "soils-water",
    name: "WSUD",
    short: "WSUD",
    blurb:
      "When water-sensitive design is an afterthought, we plug practical review into subdivision and urban layouts early enough to matter.",
    tiers: envTiers("WSUD"),
  },
  {
    id: "marine",
    region: "au",
    group: "soils-water",
    name: "Marine Plants",
    short: "Marine",
    blurb:
      "When seagrass, mangrove or intertidal habitat could block approvals, we survey, map and document the pathway with evidence you can defend.",
    tiers: envTiers("Marine plants"),
  },
  {
    id: "blue-carbon",
    region: "au",
    group: "soils-water",
    name: "Blue Carbon",
    short: "Blue Carbon",
    blurb:
      "When coastal carbon opportunities are hype-heavy, we frame pools, stakeholders and monitoring before you spend on deep feasibility.",
    tiers: envTiers("Blue carbon"),
  },
  {
    id: "species-id",
    region: "au",
    group: "ecology-biodiversity",
    name: "Plant & Animal Species Identification",
    short: "Flora & Fauna ID",
    blurb:
      "Qualified botanists and zoologists identify flora and fauna so survey data, impact assessments and offset packages rest on defensible taxonomy — not guesswork.",
    tiers: {
      diy: "Plant species ID from 3 photos (A$149.00 incl. GST). Need 3 photos to identify, if available. Free field sheet stays in the library.",
      diwy: "Guided survey pack with specialist ID review on your samples/photos.",
      dify: "Field botanist / zoologist deployment and formal species reporting.",
    },
  },
  {
    id: "ecological-survey",
    region: "au",
    group: "ecology-biodiversity",
    name: "Ecological Survey & Assessment",
    short: "Ecology",
    blurb:
      "Baseline and impact ecology work — habitat mapping, targeted surveys and clear constraint mapping your design and DA teams can use.",
    tiers: envTiers("Ecological survey"),
  },
  {
    id: "offsets",
    region: "au",
    group: "ecology-biodiversity",
    name: "Environmental & Biodiversity Offset Assessments",
    short: "Offsets",
    blurb:
      "When significant residual impacts need offsets, we assess, quantify and document options that stand up to agency and lender scrutiny.",
    tiers: envTiers("Offset assessment"),
  },
  {
    id: "protected-plants",
    region: "au",
    group: "ecology-biodiversity",
    name: "Protected Plants & Threatened Species",
    short: "Threatened spp.",
    blurb:
      "Protected plant surveys, threatened species habitat assessment and clearance pathways — scoped to jurisdiction and project risk.",
    tiers: envTiers("Protected plants"),
  },
  {
    id: "preclearance",
    region: "au",
    group: "ecology-biodiversity",
    name: "Pre-clearance & Habitat Assessment",
    short: "Pre-clearance",
    blurb:
      "Pre-clearance surveys, habitat features and fauna management notes so clearing windows don’t become programme shocks.",
    tiers: envTiers("Pre-clearance"),
  },
  {
    id: "pmav",
    region: "au",
    group: "vegetation-land",
    name: "PMAV Applications & Associated Tasks",
    short: "PMAV",
    blurb:
      "Property Map of Assessable Vegetation applications and supporting tasks — ground-truthing, mapping, agency packages and follow-through.",
    tiers: {
      diy: "PMAV readiness checklist and evidence-gathering worksheets.",
      diwy: "Guided application pack with mapping notes and specialist review.",
      dify: "End-to-end PMAV application, ground-truthing and agency liaison.",
    },
  },
  {
    id: "vegetation",
    region: "au",
    group: "vegetation-land",
    name: "Vegetation Management",
    short: "Veg management",
    blurb:
      "Vegetation management advice, regional ecosystem ground-truthing and clearing pathway support where land use change is on the table.",
    tiers: envTiers("Vegetation management"),
  },
  {
    id: "rehab",
    region: "au",
    group: "vegetation-land",
    name: "Rehabilitation & Revegetation Plans",
    short: "Rehab",
    blurb:
      "Practical rehab and revegetation plans with species, staging and success criteria that construction and land managers can implement.",
    tiers: envTiers("Rehabilitation"),
  },
  {
    id: "bushfire-risk",
    region: "au",
    group: "bushfire",
    name: "Bushfire Risk Assessment",
    short: "Bushfire risk",
    blurb:
      "Site bushfire risk assessment that informs layout, BAL outcomes and approval pathways — not a generic desktop stamp.",
    tiers: envTiers("Bushfire risk"),
  },
  {
    id: "bushfire-plan",
    region: "au",
    group: "bushfire",
    name: "Bushfire Hazard Management Plans",
    short: "BHMP",
    blurb:
      "Bushfire hazard management plans that integrate with design, landscaping and ongoing site management obligations.",
    tiers: envTiers("BHMP"),
  },
  {
    id: "cemp",
    region: "au",
    group: "construction-compliance",
    name: "Construction Environmental Management Plans",
    short: "CEMP",
    blurb:
      "CEMPs that crews can actually run — roles, controls, monitoring and hold points tied to real construction sequencing.",
    tiers: envTiers("CEMP"),
  },
  {
    id: "sbmp",
    region: "au",
    group: "construction-compliance",
    name: "Site-Based Management Plans",
    short: "SBMP / EMP",
    blurb:
      "Site-based and operational environmental management plans for delivery, maintenance and compliance reporting.",
    tiers: envTiers("Site EMP"),
  },
  {
    id: "monitoring",
    region: "au",
    group: "construction-compliance",
    name: "Environmental Monitoring & Compliance",
    short: "Monitoring",
    blurb:
      "Monitoring programmes, inspection regimes and compliance support when conditions of approval need evidence, not promises.",
    tiers: envTiers("Monitoring"),
  },
  {
    id: "weed-pest",
    region: "au",
    group: "construction-compliance",
    name: "Weed & Pest Management Plans",
    short: "Weed / pest",
    blurb:
      "Weed and pest management plans aligned with site works, biosecurity risk and long-term land management.",
    tiers: envTiers("Weed & pest"),
  },
  {
    id: "regulatory",
    region: "au",
    group: "approvals-development",
    name: "Regulatory & Approvals Strategy",
    short: "Approvals",
    blurb:
      "When triggers and agencies multiply, we map the pathway and sequence work so the programme doesn’t stall on process.",
    tiers: envTiers("Approvals strategy"),
  },
  {
    id: "urban",
    region: "au",
    group: "approvals-development",
    name: "Urban Development / Subdivision",
    short: "Urban",
    blurb:
      "When lot yield depends on environmental certainty, we package the scope developers and surveyors can actually brief and budget.",
    tiers: envTiers("Urban environmental package"),
  },
  {
    id: "due-diligence",
    region: "au",
    group: "approvals-development",
    name: "Environmental Due Diligence & Constraints",
    short: "Due diligence",
    blurb:
      "Early constraints screening for acquisition, feasibility and master planning — so risk is priced before capital is committed.",
    tiers: envTiers("Due diligence"),
  },
  {
    id: "standard-ea-mining",
    region: "au",
    group: "mining",
    name: "Standard Environmental Authority applications for mining",
    short: "Standard EA mining",
    blurb:
      "Scoped consultancy to prepare and lodge a Standard Environmental Authority application for a mining project in Australia. Queensland-style Standard EA is the usual frame. Site facts and agency requirements decide the pathway.",
    listedPriceCents: 750_000,
    listedPricePlusGst: true,
    tiers: {
      diy: "Not a DIY PDF. Use the approvals starter in the library only if you are still mapping triggers.",
      diwy: "Not a 48-hour guided pack. A guided approvals strategy is for pathway framing, not lodging a Standard EA.",
      dify: "Prepare and lodge a Standard EA application for mining. A$7,500.00 + GST.",
    },
  },

  // —— Singapore ——
  {
    id: "mep-performance-metrics",
    region: "sg",
    group: "mep-singapore",
    name: "Building Performance Metrics Calculation",
    short: "Performance metrics",
    blurb:
      "Calculate and document building performance metrics — energy intensity, cooling load, system efficiency, water use and KPIs owners and Green Mark assessors can defend.",
    tiers: mepTiers("Performance metrics"),
  },
  {
    id: "mep-energy-monitoring",
    region: "sg",
    group: "mep-singapore",
    name: "Energy Monitoring & Optimisation",
    short: "Energy M&O",
    blurb:
      "Metering strategy, BMS data review and ongoing optimisation so chiller plants, ACMV and electrical systems run to target — not just pass commissioning once.",
    tiers: mepTiers("Energy monitoring"),
  },
  {
    id: "mep-acmv",
    region: "sg",
    group: "mep-singapore",
    name: "ACMV / HVAC Design Review & Advisory",
    short: "ACMV / HVAC",
    blurb:
      "Air-conditioning and mechanical ventilation design review for tropical buildings — loads, zoning, fresh air, efficiency and maintainability.",
    tiers: mepTiers("ACMV / HVAC"),
  },
  {
    id: "mep-chiller",
    region: "sg",
    group: "mep-singapore",
    name: "Chiller Plant Efficiency & Optimisation",
    short: "Chiller plant",
    blurb:
      "Chiller plant performance assessment, sequencing and set-point optimisation to cut kW/RT waste and stabilise tropical cooling loads.",
    tiers: mepTiers("Chiller plant"),
  },
  {
    id: "mep-electrical",
    region: "sg",
    group: "mep-singapore",
    name: "Electrical Systems & Power Quality Advisory",
    short: "Electrical",
    blurb:
      "Electrical distribution, lighting power density, emergency power and power-quality checks that support reliable operations and energy reporting.",
    tiers: mepTiers("Electrical systems"),
  },
  {
    id: "mep-plumbing",
    region: "sg",
    group: "mep-singapore",
    name: "Plumbing, Sanitary & Water Efficiency",
    short: "Plumbing / water",
    blurb:
      "Plumbing and sanitary system review with water-efficiency metrics aligned to Singapore building practice and Water Efficient Building expectations.",
    tiers: mepTiers("Plumbing & water efficiency"),
  },
  {
    id: "mep-bms",
    region: "sg",
    group: "mep-singapore",
    name: "BMS, Metering & Smart Building Integration",
    short: "BMS / metering",
    blurb:
      "BMS points lists, sub-metering architecture and data pathways so performance metrics are measured continuously — not reconstructed from monthly bills.",
    tiers: mepTiers("BMS & metering"),
  },
  {
    id: "mep-green-mark",
    region: "sg",
    group: "mep-singapore",
    name: "Green Mark & Energy Code Support",
    short: "Green Mark",
    blurb:
      "Technical support for BCA Green Mark pathways and energy-related code expectations — calculations, evidence packs and gap analysis.",
    tiers: mepTiers("Green Mark"),
  },
  {
    id: "mep-modelling",
    region: "sg",
    group: "mep-singapore",
    name: "Building Energy Modelling & Simulation",
    short: "Energy modelling",
    blurb:
      "Whole-building and system energy modelling to test design options, predict EUI and justify MEP selections under Singapore climate conditions.",
    tiers: mepTiers("Energy modelling"),
  },
  {
    id: "mep-rcx",
    region: "sg",
    group: "mep-singapore",
    name: "Retro-commissioning & Existing Building Tune-up",
    short: "RCx / tune-up",
    blurb:
      "Systematic tune-up of existing MEP systems — find drift, fix control logic, restore design intent and lock gains with monitoring.",
    tiers: mepTiers("Retro-commissioning"),
  },
  {
    id: "mep-iaq",
    region: "sg",
    group: "mep-singapore",
    name: "Indoor Environmental Quality & Thermal Comfort",
    short: "IEQ / comfort",
    blurb:
      "Indoor air quality, ventilation effectiveness and thermal comfort assessment so energy savings don’t come at the cost of occupant performance.",
    tiers: mepTiers("IEQ & comfort"),
  },
  {
    id: "mep-mv",
    region: "sg",
    group: "mep-singapore",
    name: "Measurement & Verification (M&V)",
    short: "M&V",
    blurb:
      "IPMVP-aligned measurement and verification plans so energy savings claims after optimisation or retrofit are transparent and auditable.",
    tiers: mepTiers("M&V"),
  },
  {
    id: "mep-solar",
    region: "sg",
    group: "mep-singapore",
    name: "Solar PV & On-site Renewables Feasibility",
    short: "Solar / RE",
    blurb:
      "Roof and facade solar feasibility, yield estimates and integration with building electrical systems for commercial and institutional assets.",
    tiers: mepTiers("Solar PV"),
  },
  {
    id: "mep-om",
    region: "sg",
    group: "mep-singapore",
    name: "MEP O&M, Handover & FM Performance Packs",
    short: "O&M / FM",
    blurb:
      "Handover documentation, O&M frameworks and FM performance packs so operators can run systems to the metrics the design promised.",
    tiers: mepTiers("O&M / FM"),
  },
  {
    id: "mep-ashrae-l1",
    region: "sg",
    group: "mep-singapore",
    name: "ASHRAE Level I Energy Audit",
    short: "ASHRAE L1",
    blurb:
      "Walk-through energy audit (ASHRAE Level I) — high-level energy use, no/low-cost opportunities and whether a deeper Level II or III study is justified.",
    tiers: {
      diy: "Level I walk-through checklist and preliminary opportunity log.",
      diwy: "Guided Level I audit pack with specialist review of your walk-through data (~48h).",
      dify: "Full Level I audit on site — findings report and recommended next-level scope.",
    },
  },
  {
    id: "mep-ashrae-l2",
    region: "sg",
    group: "mep-singapore",
    name: "ASHRAE Level II Energy Audit",
    short: "ASHRAE L2",
    blurb:
      "Energy survey and analysis (ASHRAE Level II) — breakdown of end uses, ECMs with rough costs/savings, and prioritised recommendations for tropical buildings.",
    tiers: {
      diy: "Level II data-request and end-use breakdown worksheets.",
      diwy: "Guided Level II analysis pack with specialist interpretation of utility and plant data.",
      dify: "Full Level II audit — site survey, analysis and ECM prioritisation report.",
    },
  },
  {
    id: "mep-ashrae-l3",
    region: "sg",
    group: "mep-singapore",
    name: "ASHRAE Level III Energy Audit",
    short: "ASHRAE L3",
    blurb:
      "Detailed analysis of capital-intensive modifications (ASHRAE Level III) — investment-grade evaluation of major ECMs with robust savings and cost evidence.",
    tiers: {
      diy: "Level III scoping brief and measurement plan worksheet.",
      diwy: "Guided Level III scoping and M&V-oriented review of major ECM options.",
      dify: "Full investment-grade Level III study — detailed analysis and decision pack for CAPEX.",
    },
  },
];

export function servicesForRegion(region: ServiceRegion): ServiceOffering[] {
  return SERVICES.filter((s) => s.region === region);
}

export function groupsForRegion(region: ServiceRegion) {
  return SERVICE_GROUPS.filter((g) => g.region === region);
}

/**
 * Product wiring: each service → DIY resource, DIWY report type, DIFY enquiry.
 * Paths power service-card CTAs and keep downloads/templates aligned to the catalogue.
 */
export interface ServiceProductLinks {
  /** Resource category slug for DIY library filter */
  diyCategory?: string;
  /** Specific DIY resource slug (preferred deep link) */
  diySlug?: string;
  /** Guided report type slug (DIWY) */
  diwySlug?: string;
  /** Prefill value for contact form (DIFY) */
  difyService?: string;
}

export const SERVICE_PRODUCTS: Record<string, ServiceProductLinks> = {
  // Australia
  ass: {
    diyCategory: "ass",
    diySlug: "ass-field-checklist",
    diwySlug: "ass-management-plan",
    difyService: "AU · Acid Sulfate Soils",
  },
  esc: {
    diyCategory: "esc",
    diySlug: "esc-site-assessment-canvas",
    diwySlug: "esc-plan",
    difyService: "AU · Erosion & Sediment Control",
  },
  soils: {
    diyCategory: "soils",
    diySlug: "soil-logging-sheet",
    diwySlug: "soil-management-plan",
    difyService: "AU · Soils",
  },
  wsud: {
    diyCategory: "wsud",
    diySlug: "wsud-design-review-checklist",
    diwySlug: "wsud-design-review",
    difyService: "AU · WSUD",
  },
  marine: {
    diyCategory: "marine-plants",
    diySlug: "marine-plants-survey-template",
    diwySlug: "marine-plants-assessment",
    difyService: "AU · Marine Plants",
  },
  "blue-carbon": {
    diyCategory: "blue-carbon",
    diySlug: "blue-carbon-project-canvas",
    diwySlug: "blue-carbon-framing",
    difyService: "AU · Blue Carbon",
  },
  "species-id": {
    diyCategory: "ecology",
    diySlug: "plant-species-id",
    diwySlug: "species-id-review",
    difyService: "AU · Plant & Animal Species Identification",
  },
  "ecological-survey": {
    diyCategory: "ecology",
    diySlug: "ecological-survey-starter",
    diwySlug: "ecological-survey-brief",
    difyService: "AU · Ecological Survey & Assessment",
  },
  offsets: {
    diyCategory: "ecology",
    diySlug: "offset-assessment-canvas",
    diwySlug: "biodiversity-offset-memo",
    difyService: "AU · Environmental & Biodiversity Offset Assessments",
  },
  "protected-plants": {
    diyCategory: "ecology",
    diySlug: "protected-plants-checklist",
    diwySlug: "protected-plants-assessment",
    difyService: "AU · Protected Plants & Threatened Species",
  },
  preclearance: {
    diyCategory: "ecology",
    diySlug: "preclearance-checklist",
    diwySlug: "preclearance-pack",
    difyService: "AU · Pre-clearance & Habitat Assessment",
  },
  pmav: {
    diyCategory: "vegetation",
    diySlug: "pmav-readiness-checklist",
    diwySlug: "pmav-application-pack",
    difyService: "AU · PMAV Applications & Associated Tasks",
  },
  vegetation: {
    diyCategory: "vegetation",
    diySlug: "vegetation-management-canvas",
    diwySlug: "vegetation-management-memo",
    difyService: "AU · Vegetation Management",
  },
  rehab: {
    diyCategory: "vegetation",
    diySlug: "rehab-plan-worksheet",
    diwySlug: "rehab-plan-review",
    difyService: "AU · Rehabilitation & Revegetation Plans",
  },
  "bushfire-risk": {
    diyCategory: "bushfire",
    diySlug: "bushfire-risk-checklist",
    diwySlug: "bushfire-risk-assessment",
    difyService: "AU · Bushfire Risk Assessment",
  },
  "bushfire-plan": {
    diyCategory: "bushfire",
    diySlug: "bhmp-worksheet",
    diwySlug: "bushfire-hazard-management-plan",
    difyService: "AU · Bushfire Hazard Management Plans",
  },
  cemp: {
    diyCategory: "construction-env",
    diySlug: "cemp-structure-checklist",
    diwySlug: "cemp-review",
    difyService: "AU · Construction Environmental Management Plans",
  },
  sbmp: {
    diyCategory: "construction-env",
    diySlug: "sbmp-outline",
    diwySlug: "sbmp-review",
    difyService: "AU · Site-Based Management Plans",
  },
  monitoring: {
    diyCategory: "construction-env",
    diySlug: "monitoring-log-sheet",
    diwySlug: "monitoring-programme-brief",
    difyService: "AU · Environmental Monitoring & Compliance",
  },
  "weed-pest": {
    diyCategory: "construction-env",
    diySlug: "weed-pest-plan-sheet",
    diwySlug: "weed-pest-plan-review",
    difyService: "AU · Weed & Pest Management Plans",
  },
  regulatory: {
    diyCategory: "regulatory",
    diySlug: "approvals-strategy-starter",
    diwySlug: "approvals-strategy",
    difyService: "AU · Regulatory & Approvals Strategy",
  },
  urban: {
    diyCategory: "urban-development",
    diySlug: "urban-development-environmental-brief",
    diwySlug: "urban-environmental-package",
    difyService: "AU · Urban Development / Subdivision",
  },
  "due-diligence": {
    diyCategory: "regulatory",
    diySlug: "env-due-diligence-checklist",
    diwySlug: "env-due-diligence-memo",
    difyService: "AU · Environmental Due Diligence & Constraints",
  },
  "standard-ea-mining": {
    diyCategory: "regulatory",
    diwySlug: "approvals-strategy",
    difyService: "AU · Standard Environmental Authority applications for mining",
  },

  // Singapore MEP
  "mep-performance-metrics": {
    diyCategory: "mep-singapore",
    diySlug: "building-performance-metrics-sheet",
    diwySlug: "building-performance-metrics",
    difyService: "SG · Building Performance Metrics Calculation",
  },
  "mep-energy-monitoring": {
    diyCategory: "mep-singapore",
    diySlug: "energy-monitoring-checklist",
    diwySlug: "energy-monitoring-review",
    difyService: "SG · Energy Monitoring & Optimisation",
  },
  "mep-acmv": {
    diyCategory: "mep-singapore",
    diySlug: "acmv-design-review-checklist",
    diwySlug: "acmv-design-review",
    difyService: "SG · ACMV / HVAC Design Review & Advisory",
  },
  "mep-chiller": {
    diyCategory: "mep-singapore",
    diySlug: "chiller-plant-kpi-sheet",
    diwySlug: "chiller-plant-optimisation",
    difyService: "SG · Chiller Plant Efficiency & Optimisation",
  },
  "mep-electrical": {
    diyCategory: "mep-singapore",
    diySlug: "electrical-systems-checklist",
    diwySlug: "electrical-systems-review",
    difyService: "SG · Electrical Systems & Power Quality Advisory",
  },
  "mep-plumbing": {
    diyCategory: "mep-singapore",
    diySlug: "water-efficiency-checklist",
    diwySlug: "plumbing-water-review",
    difyService: "SG · Plumbing, Sanitary & Water Efficiency",
  },
  "mep-bms": {
    diyCategory: "mep-singapore",
    diySlug: "bms-metering-checklist",
    diwySlug: "bms-metering-review",
    difyService: "SG · BMS, Metering & Smart Building Integration",
  },
  "mep-green-mark": {
    diyCategory: "mep-singapore",
    diySlug: "green-mark-evidence-checklist",
    diwySlug: "green-mark-gap-analysis",
    difyService: "SG · Green Mark & Energy Code Support",
  },
  "mep-modelling": {
    diyCategory: "mep-singapore",
    diySlug: "energy-modelling-brief",
    diwySlug: "energy-modelling-review",
    difyService: "SG · Building Energy Modelling & Simulation",
  },
  "mep-rcx": {
    diyCategory: "mep-singapore",
    diySlug: "rcx-opportunity-checklist",
    diwySlug: "retro-commissioning-pack",
    difyService: "SG · Retro-commissioning & Existing Building Tune-up",
  },
  "mep-iaq": {
    diyCategory: "mep-singapore",
    diySlug: "ieq-comfort-checklist",
    diwySlug: "ieq-comfort-review",
    difyService: "SG · Indoor Environmental Quality & Thermal Comfort",
  },
  "mep-mv": {
    diyCategory: "mep-singapore",
    diySlug: "mv-plan-worksheet",
    diwySlug: "mv-plan-review",
    difyService: "SG · Measurement & Verification (M&V)",
  },
  "mep-solar": {
    diyCategory: "mep-singapore",
    diySlug: "solar-pv-feasibility-sheet",
    diwySlug: "solar-pv-feasibility",
    difyService: "SG · Solar PV & On-site Renewables Feasibility",
  },
  "mep-om": {
    diyCategory: "mep-singapore",
    diySlug: "mep-om-handover-checklist",
    diwySlug: "mep-om-performance-pack",
    difyService: "SG · MEP O&M, Handover & FM Performance Packs",
  },
  "mep-ashrae-l1": {
    diyCategory: "mep-singapore",
    diySlug: "ashrae-level-1-walkthrough-checklist",
    diwySlug: "ashrae-level-1-energy-audit",
    difyService: "SG · ASHRAE Level I Energy Audit",
  },
  "mep-ashrae-l2": {
    diyCategory: "mep-singapore",
    diySlug: "ashrae-level-2-data-worksheet",
    diwySlug: "ashrae-level-2-energy-audit",
    difyService: "SG · ASHRAE Level II Energy Audit",
  },
  "mep-ashrae-l3": {
    diyCategory: "mep-singapore",
    diySlug: "ashrae-level-3-scoping-worksheet",
    diwySlug: "ashrae-level-3-energy-audit",
    difyService: "SG · ASHRAE Level III Energy Audit",
  },
};

export function productLinksForService(serviceId: string): ServiceProductLinks {
  return SERVICE_PRODUCTS[serviceId] ?? {};
}

export function diyPathForService(serviceId: string): string {
  const p = productLinksForService(serviceId);
  if (p.diySlug) return `/resources/${p.diySlug}`;
  if (p.diyCategory) return `/resources?category=${encodeURIComponent(p.diyCategory)}`;
  return DELIVERY_MODEL.diy.path;
}

export function diwyPathForService(serviceId: string): string {
  const p = productLinksForService(serviceId);
  if (p.diwySlug) return `/reports?type=${encodeURIComponent(p.diwySlug)}`;
  return DELIVERY_MODEL.diwy.path;
}

export function difyPathForService(serviceId: string): string {
  const p = productLinksForService(serviceId);
  if (p.difyService) {
    return `/contact?service=${encodeURIComponent(p.difyService)}`;
  }
  return DELIVERY_MODEL.dify.path;
}

export function formatAud(cents: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(cents / 100);
}

/** Split a GST-inclusive AUD amount into ex-GST + GST (GST = 1/11 of the total). */
export function splitGstInclusive(inclGstCents: number) {
  const gst = Math.round(inclGstCents / 11);
  return {
    subtotalCents: inclGstCents - gst,
    gstCents: gst,
    totalCents: inclGstCents,
  };
}

export function addGst(exGstCents: number, rate = GST_RATE) {
  const gst = Math.round(exGstCents * rate);
  return {
    subtotalCents: exGstCents,
    gstCents: gst,
    totalCents: exGstCents + gst,
  };
}
